// useFetchProduct.ts
import fetchDb from "@/utils/customRequestFunction";
import { useEffect, useLayoutEffect, useState, useRef } from "react";

import { useAdminStore } from "@/store/useAdminStore";

export function useFetchProduct(currentProductName: string) {
   const {
      currentProduct,
      setCurrentProduct,
      fetchAllImagesInProduct,
      fetchProductOptionPairs,
   } = useAdminStore();

   // Track when we're in a loading state
   const [isLoading, setIsLoading] = useState(false);
   // Ref to track the latest product name we're fetching
   const latestProductNameRef = useRef(currentProductName);

   // Combined effect for handling product name changes and fetching
   useLayoutEffect(() => {
      // Set to undefined immediately when product name changes
      setCurrentProduct(undefined);
      setIsLoading(true);
      latestProductNameRef.current = currentProductName;

      const getProduct = async () => {
         try {
            if (!currentProductName) {
               setIsLoading(false);
               return;
            }

            const fetchedProduct = await fetchDb.get(
               `/api/main-product/name/${currentProductName}`
            );

            // Check if the product name we're fetching is still the current one
            // This prevents race conditions with multiple changes
            if (latestProductNameRef.current !== currentProductName) {
               return;
            }

            if (!fetchedProduct) {
               setCurrentProduct(null);
            } else {
               setCurrentProduct(fetchedProduct);
            }
         } catch (error) {
            console.error("Error fetching product", error);
            // Only update state if this is still the current product name
            if (latestProductNameRef.current === currentProductName) {
               setCurrentProduct(null);
            }
         } finally {
            if (latestProductNameRef.current === currentProductName) {
               setIsLoading(false);
            }
         }
      };

      getProduct();

      // Clean-up function to handle component unmount or product name change
      return () => {
         // If there's any pending state update for the previous product, we want to cancel it
         // We can't cancel the fetch, but we can prevent state updates for old product names
      };
   }, [currentProductName, setCurrentProduct]);

   // This effect remains separate as it depends on currentProduct
   useEffect(() => {
      if (!currentProduct?.id) return;
      fetchAllImagesInProduct(currentProduct.id);
      fetchProductOptionPairs(currentProduct.id);
   }, [currentProduct, fetchAllImagesInProduct, fetchProductOptionPairs]);

   return {
      product: currentProduct,
      setProduct: setCurrentProduct,
      isLoading,
   };
}
